import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import posthog from "posthog-js";
import {
  SWEEPSTAKES_DISMISSED_SESSION_STORAGE_KEY,
  SWEEPSTAKES_ENTERED_LOCAL_STORAGE_KEY,
  KLAVIYO_EMAIL_CLICK_QUERY_PARAM,
  type SweepstakesAnswerKey,
} from "@/utils/constants";
import { getAcquisition } from "@/utils/acquisition";
import {
  POPUP_VARIANT_CONFIG,
  giftForOffer,
  type PopupVariant,
} from "@/utils/popupOffers";
import { subscribeEmail } from "@/lib/keepMePosted";
import useLayoutStore from "@/stores/useLayoutStore";
import SweepstakesModal from "./SweepstakesModal";
import { preloadGiftBadges, preloadPanel } from "./popupPreload";
import { resolvePopupVariant, sessionStartedAt } from "./popupVariant";
import { nextStep, type SweepstakesStep } from "./types";

/** The longest a qualified visitor waits on the panel photo before it opens anyway. */
const PANEL_READY_GRACE_MS = 2500;

const SWEEPSTAKES_PATHS = new Set([
  "/",
  "/life-with-matic",
  "/innovation",
  "/product",
  "/accessories",
  "/accessories/[product]",
  "/blog",
]);

// Blog posts are individual static pages, so they match by prefix; the
// exclusion suppresses one exact URL.
const SWEEPSTAKES_EXCLUDED_PATHS = new Set(["/blog/matic-release-notes"]);

export default function SweepstakesTrigger() {
  const router = useRouter();
  const { pathname, asPath } = router;
  // String.split always returns at least one element.
  const cleanPath = asPath.split("?")[0]!.split("#")[0]!.replace(/\/$/, "");
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<SweepstakesStep>("intro");
  const [answer, setAnswer] = useState<SweepstakesAnswerKey | null>(null);
  const [variant, setVariant] = useState<PopupVariant | null>(null);
  const hasFired = useRef(false);
  const submittedRef = useRef(false);
  const setSweepstakesOpen = useLayoutStore(
    (state) => state.setSweepstakesOpen,
  );

  // Warm the brand fonts after hydration; the variant's panel photo preloads
  // once the variant is known.
  useEffect(() => {
    void document.fonts?.load("400 1em 'Mori Gothic'");
    void document.fonts?.load("600 1em 'Mori Gothic'");
    void document.fonts?.load("400 1em 'Trim Mono'");
  }, []);

  // Publish open state so the referral toast can yield while the modal is up.
  useEffect(() => {
    setSweepstakesOpen(isOpen);
  }, [isOpen, setSweepstakesOpen]);

  useEffect(() => {
    hasFired.current = false;

    try {
      const params = new URLSearchParams(window.location.search);
      const fromKlaviyoEmail =
        params.has(KLAVIYO_EMAIL_CLICK_QUERY_PARAM) ||
        params.get("utm_source")?.toLowerCase() === "klaviyo";
      if (
        fromKlaviyoEmail &&
        !localStorage.getItem(SWEEPSTAKES_ENTERED_LOCAL_STORAGE_KEY)
      ) {
        localStorage.setItem(
          SWEEPSTAKES_ENTERED_LOCAL_STORAGE_KEY,
          String(Date.now()),
        );
      }
    } catch (error) {
      console.error("Failed to flag sweepstakes registration:", error);
    }

    if (!SWEEPSTAKES_PATHS.has(pathname) && !pathname.startsWith("/blog/")) {
      return;
    }
    if (SWEEPSTAKES_EXCLUDED_PATHS.has(cleanPath)) return;

    const suppressed = () => {
      try {
        if (sessionStorage.getItem(SWEEPSTAKES_DISMISSED_SESSION_STORAGE_KEY)) {
          return true;
        }
        return Boolean(
          localStorage.getItem(SWEEPSTAKES_ENTERED_LOCAL_STORAGE_KEY),
        );
      } catch (error) {
        console.error("Failed to read sweepstakes storage:", error);
        return false;
      }
    };

    // Checked before the flag read, so visitors who can't see the popup again
    // are never counted as exposed to a variant.
    if (suppressed()) return;

    const abort = new AbortController();
    let scrolled = window.scrollY > 0;
    let delayTimer: number | undefined;
    let graceTimer: number | undefined;
    let open: (() => void) | undefined;

    // Opens once the visitor has scrolled AND spent the variant's minimum time
    // on site, whichever comes last.
    const onScroll = () => {
      if (window.scrollY <= 0) return;
      scrolled = true;
      window.removeEventListener("scroll", onScroll);
      open?.();
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    void resolvePopupVariant(abort.signal).then((resolved) => {
      if (abort.signal.aborted) return;
      const { delayMs, layout, offer } = POPUP_VARIANT_CONFIG[resolved];
      const startedAt = sessionStartedAt();

      // The popup waits for its photo so it never opens on an empty panel,
      // but only up to a grace period, so a slow network can't hold it back.
      let panelReady = false;
      void preloadPanel(resolved).then(() => {
        panelReady = true;
        open?.();
      });

      open = () => {
        if (hasFired.current || !scrolled) return;
        const remaining = delayMs - (Date.now() - startedAt);
        if (remaining > 0) {
          if (delayTimer === undefined) {
            delayTimer = window.setTimeout(() => {
              delayTimer = undefined;
              open?.();
            }, remaining);
          }
          return;
        }
        if (!panelReady) {
          if (graceTimer === undefined) {
            graceTimer = window.setTimeout(() => {
              panelReady = true;
              open?.();
            }, PANEL_READY_GRACE_MS);
          }
          return;
        }
        hasFired.current = true;
        if (suppressed()) return;
        setVariant(resolved);
        setStep("intro");
        setAnswer(null);
        submittedRef.current = false;
        setIsOpen(true);
        posthog.capture("sweepstakes_impression", {
          path: cleanPath,
          popup_variant: resolved,
          popup_layout: layout,
          popup_offer: offer.kind,
        });
        if (offer.kind === "quiz") void preloadGiftBadges();
      };
      // Covers a scroll that landed before the variant resolved, or during
      // hydration before the listener attached.
      if (window.scrollY > 0) scrolled = true;
      open();
    });

    return () => {
      abort.abort();
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(delayTimer);
      window.clearTimeout(graceTimer);
    };
  }, [pathname, cleanPath]);

  /**
   * One advance per step. `setStep`'s updater composes, so two taps landing in the
   * same frame — trivial on a phone — used to walk two steps at once: a double-tap
   * on a quiz pill went quiz → email → success, showing the confirmation while the
   * email form never rendered and no entry was ever submitted. The pre-redesign
   * flow was safe because its one-shot ref sat on the answer handler; the ref moved
   * to the email step when the order changed, leaving the earlier steps open.
   *
   * Keyed on the step it was called from rather than a plain boolean, so a second
   * tap is ignored while a legitimate advance from the next step still works.
   */
  const advancedFromRef = useRef<SweepstakesStep | null>(null);

  const variantConfig = variant ? POPUP_VARIANT_CONFIG[variant] : null;
  const gift = variantConfig ? giftForOffer(variantConfig.offer, answer) : null;
  const variantProps =
    variant && variantConfig
      ? {
          popup_variant: variant,
          popup_layout: variantConfig.layout,
          popup_offer: variantConfig.offer.kind,
          ...(gift ? { popup_gift: gift } : {}),
        }
      : {};

  const advance = () => {
    if (advancedFromRef.current === step) {
      return;
    }
    advancedFromRef.current = step;

    setStep((current) => {
      const next = nextStep(current) ?? current;
      if (next !== current) {
        posthog.capture("sweepstakes_step_advanced", {
          from: current,
          to: next,
          ...variantProps,
        });
      }
      return next;
    });
  };

  const handleEntered = (selected: SweepstakesAnswerKey | null) => {
    posthog.capture("sweepstakes_entered", {
      answer: selected ?? "none",
      ...variantProps,
    });
    try {
      localStorage.setItem(
        SWEEPSTAKES_ENTERED_LOCAL_STORAGE_KEY,
        String(Date.now()),
      );
    } catch (error) {
      console.error("Failed to write sweepstakes localStorage:", error);
    }
  };

  const handleEmail = (selectedEmail: string) => {
    if (submittedRef.current) return;
    submittedRef.current = true;

    posthog.capture("sweepstakes_submit_attempt", {
      answer: answer ?? "none",
      ...variantProps,
    });

    // Advance the UI up front — mobile round-trips to /api/keep-me-posted take
    // hundreds of ms and the previous await-based path left users staring at a
    // disabled form while they waited, which was crushing signup completions.
    handleEntered(answer);
    advance();

    void subscribeEmail(
      {
        email: selectedEmail,
        sweepstakesAnswer: answer ?? "none",
        ...(variant ? { popupVariant: variant } : {}),
        acquisition: getAcquisition("sweepstakes"),
      },
      { keepalive: true },
    )
      .then((result) => {
        if (result.ok) {
          posthog.identify(selectedEmail, { email: selectedEmail });
          posthog.capture("sweepstakes_submit_success", variantProps);
        } else {
          posthog.capture("sweepstakes_submit_error", {
            status: result.status,
            ...variantProps,
          });
          console.error(
            "Failed to submit sweepstakes entry:",
            result.status,
            result.message,
          );
        }
      })
      .catch((err) => {
        posthog.capture("sweepstakes_submit_error", {
          status: 0,
          message: err instanceof Error ? err.message : String(err),
          ...variantProps,
        });
        console.error("Failed to submit sweepstakes entry:", err);
      });
  };

  const handleAnswer = (selected: SweepstakesAnswerKey | null) => {
    if (advancedFromRef.current === step) {
      return;
    }
    setAnswer(selected);
    advance();
  };

  const close = () => {
    posthog.capture("sweepstakes_dismissed", { step, ...variantProps });
    try {
      sessionStorage.setItem(SWEEPSTAKES_DISMISSED_SESSION_STORAGE_KEY, "true");
    } catch (error) {
      console.error("Failed to write sweepstakes sessionStorage:", error);
    }
    setIsOpen(false);
    setStep("intro");
    setAnswer(null);
    submittedRef.current = false;
    advancedFromRef.current = null;
  };

  const handleShop = () => {
    close();
    void router.push("/product").catch((error) => {
      console.error("Failed to navigate to the product page:", error);
    });
  };

  if (!variant || !variantConfig) return null;

  return (
    <SweepstakesModal
      isOpen={isOpen}
      variant={variant}
      layout={variantConfig.layout}
      offer={variantConfig.offer}
      gift={gift}
      step={step}
      onAdvance={advance}
      onEmail={handleEmail}
      onAnswer={handleAnswer}
      onClose={close}
      onShop={handleShop}
    />
  );
}
